// src/hooks/useTheme.js
import { useTheme as useThemeContext } from '../contexts/ThemeContext';

/**
 * Custom hook to access theme context
 * Provides theme state and functions for managing themes
 * 
 * @returns {Object} Theme context
 * @property {string} currentTheme - Current theme ID (e.g., 'purple-light')
 * @property {boolean} isTransitioning - Whether theme is currently transitioning
 * @property {boolean} isLoading - Whether theme is loading
 * @property {Array} availableThemes - Array of all available theme metadata
 * @property {Function} setTheme - Function to change theme and persist (async)
 * @property {Function} previewTheme - Function to preview theme without persisting (sync)
 * @property {Function} getTheme - Get current theme metadata
 * @property {Function} getThemeById - Get theme metadata by ID
 * @property {Function} isDarkTheme - Check if current theme is dark
 * @property {Function} toggleDarkMode - Toggle between light/dark variants
 * @property {Function} getThemesByCategory - Get themes by category
 * @property {Function} getThemesByMode - Get themes by light/dark mode
 * @property {Function} getCategories - Get all theme categories
 * 
 * @example
 * const { currentTheme, setTheme, previewTheme, isDarkTheme } = useTheme();
 * 
 * // Preview theme (live, no persistence)
 * previewTheme('blue-dark');
 * 
 * // Change and save theme permanently
 * await setTheme('blue-dark', true);
 * 
 * // Get current theme info
 * const theme = getTheme();
 * console.log(theme.name); // "Purple Light"
 * 
 * // Check if dark mode
 * if (isDarkTheme()) {
 *   // Dark mode specific logic
 * }
 * 
 * // Toggle dark/light
 * toggleDarkMode();
 */
export const useTheme = () => {
  return useThemeContext();
};

export default useTheme;